import { useState } from 'react';
import type { ReactNode } from 'react';

import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { ApiError } from '@/shared/services/api/api-error';
import { queryKeys } from '@/shared/services/api/query-keys';
import {
  CACHE_MAX_AGE_MS,
  PERSIST_VERSION,
  createCachePersister,
} from '@/shared/services/query/cache-persister';

/**
 * Réglages de React Query pour un réseau rare et facturé au mégaoctet.
 *
 * La regle d'or : **on ne retélécharge jamais ce qu'on vient de voir**. Ce qui
 * a été chargé s'affiche depuis le cache, y compris apres un redémarrage
 * puisque le cache est persisté sur disque ; le réseau ne sert qu'a rafraichir
 * ce qui est vraiment périmé. La fraicheur des écrans chauds (commandes,
 * messages, notifications), elle, ne dépend pas de ces délais : le canal temps
 * réel invalide précisément ce qui change, a l'instant ou cela change.
 */
const STALE_TIME_MS = 5 * 60_000;

/**
 * Les référentiels ne bougent qu'a la sortie d'un déploiement : les recharger
 * a chaque ouverture d'écran était du réseau jeté. Un jour de fraicheur ; le
 * détail (les articles) vaut pour toutes les catégories, d'ou le préfixe nu.
 */
const REFERENTIAL_STALE_TIME_MS = 24 * 60 * 60_000;
const REFERENTIAL_KEYS: ReadonlyArray<ReadonlyArray<string>> = [
  queryKeys.catalog.categories,
  ['catalog', 'articles'],
  queryKeys.mobileMoney.operators,
  queryKeys.billing.plans,
];

function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        // Aligné sur la durée du cache disque : une entrée sortie de la
        // mémoire serait aussi perdue pour le prochain démarrage.
        gcTime: CACHE_MAX_AGE_MS,
        retry(failureCount, error) {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
        // Le retour du réseau est annoncé par le socket, qui invalide déja
        // tout ce qui compte apres une coupure : laisser React Query refaire
        // la meme chose paierait chaque rechargement deux fois.
        refetchOnReconnect: false,
      },
      mutations: {
        // Une action de l'utilisateur ne se rejoue jamais toute seule : un
        // double envoi de commande serait pire que l'échec.
        retry: false,
        // Et hors ligne, elle échoue tout de suite, avec son message : jamais
        // de file d'attente silencieuse qui partirait au retour du réseau.
        networkMode: 'always',
      },
    },
  });

  for (const key of REFERENTIAL_KEYS) {
    client.setQueryDefaults(key, { staleTime: REFERENTIAL_STALE_TIME_MS });
  }

  return client;
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);
  const [persistOptions] = useState(() => ({
    persister: createCachePersister(),
    maxAge: CACHE_MAX_AGE_MS,
    buster: PERSIST_VERSION,
  }));

  return (
    <PersistQueryClientProvider client={client} persistOptions={persistOptions}>
      {children}
    </PersistQueryClientProvider>
  );
}
